import { useEffect, useState } from "preact/hooks"
import { format, parseISO } from "date-fns"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { toast } from "@/components/ui/use-toast"
import { AffectedDeduction, PreviewResponse, ProposedChange, apply_change, preview_change } from "./api"

interface PreviewDialogProps {
  /** The change to preview; null closes the dialog. */
  change: ProposedChange | null
  /** Short human description of the change, shown in the title. */
  title: string
  onClose: () => void
  /** Called after the change was applied successfully. */
  onApplied: () => void
}

/**
 * Preview-and-confirm: shows exactly which deductions the change affects (and how)
 * before anything is written. Confirm sends the identical change to the apply endpoint.
 */
export function PreviewDialog({ change, title, onClose, onApplied }: PreviewDialogProps) {
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [applying, setApplying] = useState(false)

  useEffect(
    function load_preview() {
      setPreview(null)
      if (!change) return
      setLoading(true)
      void preview_change(change).then((res) => {
        setLoading(false)
        if (res.ok) {
          setPreview(res.value.data)
        } else {
          toast({ title: "Could not preview change", description: res.error.message, variant: "destructive" })
          onClose()
        }
      })
    },
    [JSON.stringify(change)],
  )

  async function confirm() {
    if (!change) return
    setApplying(true)
    const res = await apply_change(change)
    setApplying(false)
    if (res.ok) {
      const n = res.value.data.updated_deductions
      toast({ title: "Change applied", description: n > 0 ? `${n} deduction${n === 1 ? "" : "s"} updated.` : "Saved." })
      onApplied()
      onClose()
    } else {
      toast({ title: "Could not apply change", description: res.error.message, variant: "destructive" })
    }
  }

  return (
    <Dialog open={change !== null} onOpenChange={(open: boolean) => !open && onClose()}>
      {/* min-w-0 on the body keeps the table inside the dialog (grid items default to min-width:auto) */}
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden grid-rows-[auto_1fr_auto]">
        <DialogHeader>
          <DialogTitle class="pr-8">{title}</DialogTitle>
          <DialogDescription>Review what this change will do before confirming. Nothing is saved until you confirm.</DialogDescription>
        </DialogHeader>

        {loading || !preview ? (
          <div class="text-sm text-gray-500 py-8 text-center">Checking affected deductions…</div>
        ) : (
          <div class="space-y-4 min-w-0 min-h-0 overflow-y-auto">
            {preview.warnings.length > 0 && (
              <ul class="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
                {preview.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
            {preview.total_affected === 0 ? (
              <p class="text-sm text-gray-600">
                No existing deductions change category. The rule change only affects how future deductions are categorized.
              </p>
            ) : (
              <div>
                <p class="text-sm text-gray-600 mb-2">
                  <span class="font-medium text-gray-900">{preview.total_affected}</span> deduction
                  {preview.total_affected === 1 ? "" : "s"} will change category
                  {preview.sample.length < preview.total_affected ? ` (showing the first ${preview.sample.length})` : ""}:
                </p>
                <div class="overflow-x-auto overflow-y-auto max-h-[45vh] border rounded-md">
                  <table class="min-w-full divide-y divide-gray-200 text-sm">
                    <thead class="bg-gray-50 sticky top-0">
                      <tr>
                        <Th>Invoice #</Th>
                        <Th>Date</Th>
                        <Th>Amount</Th>
                        <Th>Current category</Th>
                        <Th>New category</Th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      {preview.sample.map((d) => (
                        <AffectedRow key={d.id} deduction={d} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={applying}>
            Cancel
          </Button>
          <Button onClick={confirm as any} disabled={loading || applying || !preview}>
            {applying ? "Applying…" : "Confirm change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Th({ children }: { children: preact.ComponentChildren }) {
  return <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{children}</th>
}

function category_label(name: string | null, description: string | null): string {
  if (!name) return "Uncategorized"
  return description && description !== name ? `${name} — ${description}` : name
}

function AffectedRow({ deduction: d }: { deduction: AffectedDeduction }) {
  const amount = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(d.invoice_amount)
  return (
    <tr>
      <td class="px-3 py-1.5 whitespace-nowrap font-mono text-xs">{d.invoice_number}</td>
      <td class="px-3 py-1.5 whitespace-nowrap">{format(parseISO(d.invoice_date), "MMM d, yyyy")}</td>
      <td class="px-3 py-1.5 whitespace-nowrap tabular-nums">{amount}</td>
      <td class="px-3 py-1.5">{category_label(d.current_name, d.current_description)}</td>
      <td class="px-3 py-1.5">
        {category_label(d.new_name, d.new_description)}
        {d.manually_categorized && (
          <span class="ml-1 text-xs rounded-full bg-blue-50 px-1.5 py-0.5 text-blue-700" title="This deduction was categorized manually; it follows the rename/merge.">
            manual
          </span>
        )}
      </td>
    </tr>
  )
}
